import React, { useMemo } from "react";
import styles from "./BillingPlanFeatureList.module.css";

// Returns the small italic note shown above the bullet list.
// e.g. featureTitle.featureTitle = "Includes everything in Free"  →  "Includes everything in Free"
// e.g. featureTitle = {}  →  ""  (nothing shown)
function getPlanSubtitleNote(billingPlan) {
  const featureTitleObject = billingPlan?.featureTitle;
  if (!featureTitleObject || typeof featureTitleObject.featureTitle !== "string") return "";
  return featureTitleObject.featureTitle.trim();
}

// Returns the list of bullet strings to display.
// Prefers featureTitle.planFeatures  e.g. ["Unlimited SKUs", "CSV export", "Reorder deadlines"]
// Falls back to features[].description  e.g. [{ description: "Unlimited SKUs" }, ...]
function getPlanFeatureBullets(billingPlan) {
  const featureTitleObject = billingPlan?.featureTitle;
  const hasPlanFeatures =
    featureTitleObject &&
    Array.isArray(featureTitleObject.planFeatures) &&
    featureTitleObject.planFeatures.length > 0;

  if (hasPlanFeatures) {
    return featureTitleObject.planFeatures.map((bullet) => String(bullet).trim()).filter(Boolean);
  }

  // Legacy shape: features is a flat array of objects with a description string.
  return (billingPlan?.features ?? []).map((feature) => feature?.description).filter(Boolean);
}

const BillingPlanFeatureList = ({
  billingPlan,
  // Unique per card to avoid duplicate DOM ids when multiple plans are on the page.
  listId,
  // "onboarding" = billing plan selection page, "account" = account/billing details page.
  variant = "onboarding",
  // Pass true when the subtitle is already rendered elsewhere (e.g. above the plan name on the billing page).
  hideSubtitle = false,
}) => {
  const subtitleNote = useMemo(() => getPlanSubtitleNote(billingPlan), [billingPlan]);
  const featureBullets = useMemo(() => getPlanFeatureBullets(billingPlan), [billingPlan]);

  if (!subtitleNote && featureBullets.length === 0) return null;

  const variantClass =
    variant === "account" ? styles.variantAccount : styles.variantOnboarding;

  return (
    <div className={variantClass}>
      {/* Small italic line above the list — skipped when already shown above the plan name */}
      {subtitleNote && !hideSubtitle ? (
        <p className={styles.featureSubtitle}>{subtitleNote}</p>
      ) : null}

      {featureBullets.length > 0 ? (
        <>
          {variant === "onboarding" ? (
            <>
              <hr className={styles.divider} />
              <p className={styles.includedTitle}>What's included</p>
            </>
          ) : null}
          <ul id={listId} className={styles.featureList}>
            {featureBullets.map((bulletText, index) => (
              <li key={index} className={styles.featureRow}>
                {variant === "onboarding" ? (
                  <span className={styles.check} aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="16" height="16">
                      <path
                        d="M3.2 8.4 6.3 11.5 12.8 4.5"
                        fill="none"
                        stroke="#1f8a4c"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                ) : null}
                <span>{bulletText}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
};

export default BillingPlanFeatureList;
